import React from 'react';
import {
  Box,
  Flex,
  Text,
  IconButton,
  Heading,
  Image,
  useColorModeValue,
  Link
} from '@chakra-ui/react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Plus, LogOut, LogIn, UserPlus, User, BookCheck, Library, Brain, Info, ChartBarIcon, BookOpen, Layers, FileText } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { SidebarItem } from './SidebarItem';

/**
 * Sidebar component with collapsible functionality
 * 
 * @param {boolean} isCollapsed - Whether the sidebar is collapsed
 * @param {function} toggleSidebar - Function to toggle sidebar collapsed state
 */
const Sidebar = ({ isCollapsed, toggleSidebar }) => {
  const sidebarBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const activeBg = useColorModeValue('teal.50', 'teal.900');
  const activeColor = useColorModeValue('teal.700', 'teal.200');
  // Use location to determine active route

  const location = useLocation();

  const { logout, user } = useAuth()
  const handleLogout = () => {
    logout()
  }
  // Navigation items with their paths and icons
  const navItems = [
    {
      name: 'Home',
      path: '/dashboard',
      icon: (
        <Box as="svg" w="20px" h="20px" viewBox="0 0 24 24">
          <path
            fill="currentColor"
            d="M12 2.1L1 12h3v9h7v-6h2v6h7v-9h3L12 2.1zm0 2.691l6 5.4V19h-3v-6H9v6H6v-8.809l6-5.4z"
          />
        </Box>
      )
    },
    {
      name: 'Read Quran',
      path: '/quran',
      icon: (
        <Box as="svg" w="20px" h="20px" viewBox="0 0 24 24">
          <BookOpen />
        </Box>
      ),
      children: [
        { name: 'By Surah', path: '/surahs', icon: <BookOpen size={16} /> },
        { name: 'By Juz', path: '/juz', icon: <Layers size={16} /> },
        { name: 'By Page', path: '/pages/1', icon: <FileText size={16} /> },
      ]
    },
    {
      name: 'Resources',
      path: '/resources',
      icon: (
        <Box as="svg" w="20px" h="20px" viewBox="0 0 24 24">
          <Library />
        </Box>
      )
    },
    {
      name: 'Models',
      path: '/models',
      icon: (
        <Box as="svg" w="20px" h="20px" viewBox="0 0 24 24">
          <Brain />
        </Box>
      )
    }
  ];

  if (user?.role === 'admin') {
    navItems.push(
      {
        name: 'Management',
        path: '/admin',
        icon: (
          <Box as="svg" w="20px" h="20px" viewBox="0 0 24 24">
            <BookCheck />
          </Box>
        ),
        children: [
              {
                name: 'Model Verification',
                path: '/models?view=pending',
                icon: <Brain size={16} />
              },
              {
                name: 'Resource Endorsement',
                path: '/resources?view=review',
                icon: <Library size={16} />
              },
              {
                name: 'User Management',
                path: '/admin/user',
                icon: <BookCheck size={16} /> // your preferred icon
              },
            ]
      }
    )
  } 

  if (user?.role === 'expert') {
    navItems.push(
      {
        name: 'Verification',
        path: '/expert',
        icon: (
          <Box as="svg" w="20px" h="20px" viewBox="0 0 24 24">
            <BookCheck />
          </Box>
        ),
        children: [
              {
                name: 'Ayah Verification',
                path: '/expert/ayah-verification',
                icon: <BookCheck size={16} /> // your preferred icon
              },
              {
                name: 'Trans. Verification',
                path: '/expert/translation-verification',
                icon: <BookCheck size={16} /> // your preferred icon
              },
              {
                name: 'Resource Endorsement',
                path: '/resources?view=review',
                icon: <Library size={16} />
              },
              {
                name: 'Model Verification',
                path: '/models?view=pending',
                icon: <Brain size={16} />
              },
              {
                name: 'Audio Verification',
                path: '/expert/audio-verification',
                icon: <BookCheck size={16} />
              }
            ]
      }
    )
  }

  if (user?.role === 'super_expert') {
    navItems.push(
      {
        name: 'Approval',
        path: '/verifier',
        icon: (
          <Box as="svg" w="20px" h="20px" viewBox="0 0 24 24">
            <BookCheck />
          </Box>
        ),
        children: [
              {
                name: 'Ayah Approval',
                path: '/verifier/ayah-verification',
                icon: <BookCheck size={16} /> // your preferred icon
              },
              {
                name: 'Trans. Approval',
                path: '/verifier/translation-verification',
                icon: <BookCheck size={16} /> // your preferred icon
              },
              {
                name: 'Resource Endorsement',
                path: '/resources?view=review',
                icon: <Library size={16} />
              },
              {
                name: 'Model Verification',
                path: '/models?view=pending',
                icon: <Brain size={16} />
              },
              {
                name: 'Audio Approval',
                path: '/verifier/audio-verification',
                icon: <BookCheck size={16} />
              }
            ]
      }
    )

    navItems.push(
      {
        name: 'Report',
        path: '/report',
        icon: (
          <Box as="svg" w="20px" h="20px" viewBox="0 0 24 24">
            <ChartBarIcon />
          </Box>
        ),
        children: [
              {
                name: 'Ayah Report',
                path: '/report/ayah',
                icon: <ChartBarIcon size={16} />
              },
              {
                name: 'Translation Report',
                path: '/report/translation',
                icon: <ChartBarIcon size={16} />
              },
            ]
      }
    )
  }

  // Visitors who are not logged in only see the public pages
  const visibleItems = user
    ? navItems
    : navItems.filter((item) => ['/dashboard', '/quran', '/resources', '/models'].includes(item.path));

  // Check if a path is active (current location)
  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <Box
      w={isCollapsed ? "60px" : "250px"}
      borderRight="1px"
      borderColor={borderColor}
      bg={sidebarBg}
      py={4}
      position="fixed"
      h="100%"
      transition="width 0.2s ease"
      zIndex="10"
      display="flex"
      flexDirection="column"
    >
      <Flex px={4} mb={8} align="center" justify={isCollapsed ? "center" : "space-between"}>
        {!isCollapsed && (
          <Flex as={RouterLink} to="/" align="center" gap={2} minW={0}>
            <Image src={`${process.env.PUBLIC_URL}/brand/cqr-ai-hub-logo.jpg`} alt="CQR AI Hub" boxSize="38px" objectFit="contain" flexShrink={0} />
            <Heading fontSize="sm" lineHeight="short" color="teal.700">CQR Quranic AI Datahub</Heading>
          </Flex>
        )}
        <IconButton
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          icon={isCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          onClick={toggleSidebar}
          size="sm"
          variant="ghost"
        />
      </Flex>

      {/* Create Button */}
      {user && (
      <Flex direction="column" px={4} mb={2} align={isCollapsed ? "center" : "flex-start"}>
        <Box
          as={RouterLink}
          to="/create"
          display="flex"
          alignItems="center"
          justifyContent={isCollapsed ? "center" : "flex-start"}
          width={isCollapsed ? "40px" : "full"}
          borderRadius="md"
          border="1px solid"
          borderColor="teal.500"
          color={isActive('/create') ? "white" : "teal.500"}
          bg={isActive('/create') ? "teal.500" : "transparent"}
          p={2}
          _hover={{
            bg: isActive('/create') ? "teal.600" : "teal.50"
          }}
          transition="all 0.2s"
        >
          <Plus size={18} />
          {!isCollapsed && <Text ml={2}>Create</Text>}
        </Box>
      </Flex>
      )}

      {/* Navigation Items - Main Content */}
      <Flex
        direction="column"
        px={4}
        align={isCollapsed ? "center" : "flex-start"}
        flex="1"
      >
        {visibleItems.map((item) => (
          <SidebarItem 
            key={item.name}
            item={item}
            isCollapsed={isCollapsed}
            activePath={location.pathname}
          />
        ))}
      </Flex>

      {/* Logout Button - Fixed at Bottom */}
      <Flex
        direction="column"
        px={4}
        mt="auto"
        pt={4}
        borderTop="1px"
        borderColor={borderColor}
        align={isCollapsed ? "center" : "flex-start"}
      >
        <Link
          as={RouterLink}
          to={'/about'}
          display="flex"
          width={isCollapsed ? "40px" : "full"}
          py={2}
          px={3}
          alignItems="center"
          borderRadius="md"
          _hover={{ bg: hoverBg, color: "blue.500" }}
          cursor="pointer"
          fontWeight="medium"
        >
          <Info size={20} style={{ marginRight: isCollapsed ? 0 : 12 }} />
          {!isCollapsed && <Text>About</Text>}
        </Link>
        {user ? (
          <>
            <Link
              as={RouterLink}
              to={'/profile'}
              display="flex"
              width={isCollapsed ? "40px" : "full"}
              py={2}
              px={3}
              alignItems="center"
              borderRadius="md"
              _hover={{ bg: hoverBg, color: "blue.500" }}
              cursor="pointer"
              fontWeight="medium"
            >
              <User size={20} style={{ marginRight: isCollapsed ? 0 : 12 }} />
              {!isCollapsed && <Text>Profile</Text>}
            </Link>
            <Link
              onClick={handleLogout}
              display="flex"
              width={isCollapsed ? "40px" : "full"}
              py={2}
              px={3}
              alignItems="center"
              borderRadius="md"
              _hover={{ bg: hoverBg, color: "red.500" }}
              cursor="pointer"
              fontWeight="medium"
            >
              <LogOut size={20} style={{ marginRight: isCollapsed ? 0 : 12 }} />
              {!isCollapsed && <Text>Logout</Text>}
            </Link>
          </>
        ) : (
          <>
            <Link
              as={RouterLink}
              to={'/login'}
              display="flex"
              width={isCollapsed ? "40px" : "full"}
              py={2}
              px={3}
              alignItems="center"
              borderRadius="md"
              _hover={{ bg: hoverBg, color: "teal.500" }}
              fontWeight="medium"
            >
              <LogIn size={20} style={{ marginRight: isCollapsed ? 0 : 12 }} />
              {!isCollapsed && <Text>Log in</Text>}
            </Link>
            <Link
              as={RouterLink}
              to={'/register'}
              display="flex"
              width={isCollapsed ? "40px" : "full"}
              py={2}
              px={3}
              alignItems="center"
              borderRadius="md"
              _hover={{ bg: hoverBg, color: "teal.500" }}
              fontWeight="medium"
            >
              <UserPlus size={20} style={{ marginRight: isCollapsed ? 0 : 12 }} />
              {!isCollapsed && <Text>Register</Text>}
            </Link>
          </>
        )}
      </Flex>
    </Box>
  );
};

export default Sidebar;
